import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { LoraSelectorPopup } from '@/components/lora-selector-popup'
import { PlusIcon, XIcon, CopyIcon, CheckIcon } from 'lucide-react'
import type { LoraPreviewDTO } from '@/lib/types'
import { toast } from 'sonner'
import { useState } from 'react'

interface LoraSidebarProps {
  availableLoras: LoraPreviewDTO[]
  selectedLoras: Record<number, number>
  onAddLora: (lora: LoraPreviewDTO) => void
  onRemoveLora: (loraId: number) => void
  onUpdateStrength: (loraId: number, strength: number) => void
}

export function LoraSidebar({
  availableLoras,
  selectedLoras,
  onAddLora,
  onRemoveLora,
  onUpdateStrength,
}: LoraSidebarProps) {
  const [copiedTriggerWords, setCopiedTriggerWords] = useState<Set<string>>(new Set())

  const getLoraById = (id: number): LoraPreviewDTO | undefined => {
    return availableLoras.find(lora => lora.id === id)
  }

  const handleCopyTriggerWord = async (triggerWord: string, loraName: string) => {
    try {
      await navigator.clipboard.writeText(`${triggerWord} (from ${loraName})`)
      toast.success(`Copied "${triggerWord}" to clipboard`)

      // Add to copied set and remove after 2 seconds
      setCopiedTriggerWords(prev => new Set(prev).add(triggerWord))
      setTimeout(() => {
        setCopiedTriggerWords(prev => {
          const next = new Set(prev)
          next.delete(triggerWord)
          return next
        })
      }, 2000)
    } catch (error) {
      // Fallback for browsers that don't support the Clipboard API
      const textArea = document.createElement('textarea')
      textArea.value = triggerWord
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand('copy')
        toast.success(`Copied "${triggerWord}" to clipboard`)

        // Add to copied set and remove after 2 seconds
        setCopiedTriggerWords(prev => new Set(prev).add(triggerWord))
        setTimeout(() => {
          setCopiedTriggerWords(prev => {
            const next = new Set(prev)
            next.delete(triggerWord)
            return next
          })
        }, 2000)
      } catch (fallbackError) {
        toast.error('Failed to copy to clipboard')
      } finally {
        document.body.removeChild(textArea)
      }
    }
  }

  const selectedLorasArray = Object.entries(selectedLoras).map(([id, strength]) => ({
    lora: getLoraById(Number(id)),
    strength,
    id: Number(id),
  })).filter(item => item.lora !== undefined)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>LoRAs</Label>
        <span className="text-xs text-muted-foreground">
          {selectedLorasArray.length} selected
        </span>
      </div>

      {/* Add LoRA Button */}
      <LoraSelectorPopup
        availableLoras={availableLoras}
        selectedLoras={selectedLoras}
        onAddLora={onAddLora}
        onRemoveLora={onRemoveLora}
      >
        <Button variant="outline" className="w-full">
          <PlusIcon className="h-4 w-4 mr-2" />
          Add LoRA
        </Button>
      </LoraSelectorPopup>

      {/* Selected LoRAs List */}
      <div className="space-y-2">
        {selectedLorasArray.length > 0 ? (
          <div className="p-2 space-y-2 border border-border rounded-md bg-background">
            {selectedLorasArray.map(({ lora, strength, id }) => (
                <div
                  key={id}
                  className="border border-border bg-card/60 p-3 text-sm space-y-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 mr-2">
                      <h4 className="font-medium text-sm leading-tight break-words">
                        {lora?.civitai_url ? (
                          <a
                            href={lora.civitai_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent hover:text-accent/80 underline decoration-accent/30 hover:decoration-accent/60 transition-colors"
                            title={`View ${lora.display_name} on Civitai`}
                          >
                            {lora.display_name}
                          </a>
                        ) : (
                          lora?.display_name
                        )}
                      </h4>
                      {lora?.trigger_words && lora.trigger_words.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1 leading-tight break-words">
                          <div className="flex flex-wrap gap-1">
                            {lora.trigger_words.map((triggerWord, index) => (
                              <button
                                key={index}
                                onClick={() => handleCopyTriggerWord(triggerWord, lora.display_name)}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-muted/50 hover:bg-muted/80 rounded text-xs transition-colors cursor-pointer group"
                                title={`Click to copy "${triggerWord}"`}
                              >
                                <span className="group-hover:text-accent">{triggerWord}</span>
                                {copiedTriggerWords.has(triggerWord) ? (
                                  <CheckIcon className="h-2.5 w-2.5 text-green-500 animate-pulse" />
                                ) : (
                                  <CopyIcon className="h-2.5 w-2.5 text-accent" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveLora(id)}
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground flex-shrink-0"
                    >
                      <XIcon className="h-3 w-3" />
                    </Button>
                  </div>

                  <div className="space-y-2 min-w-0">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">
                        Strength
                      </Label>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {strength.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-full min-w-0">
                      <Slider
                        min={0}
                        max={2}
                        step={0.1}
                        value={[strength]}
                        onValueChange={(value) => onUpdateStrength(id, value[0])}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground border border-dashed border-border rounded-lg">
            <p className="text-sm">No LoRAs selected</p>
            <p className="text-xs mt-1">Click "Add LoRA" to get started</p>
          </div>
        )}
      </div>
    </div>
  )
}